import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';

import beDepositRefundInfo from 'actions/payment/beDepositRefundInfo.action';
import determineErrorMessage from 'bl/response/determineErrorMessage.bl';
import WizardModal from 'components/dialog/WizardModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import CurrencySign from 'components/price/CurrencySign.component';
import fromDtoToDepositRefundInfo from 'dto/refunds/fromDtoToDepositRefundInfo.dto';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useTranslation } from 'providers/GILT.provider';
import asArray from 'utils/arrays/asArray.util';
import fromBeToBody from 'utils/converters/backend/fromBeToBody.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import DepositRefundItem from 'settings/balance-sheet/DepositRefundItem.component';

import CN from './DepositRefundOnlineWizard.module.scss';

const CTAG = 'DepositRefundOnlineWizard';
const L = logger(CTAG);

const STATE = Object.freeze({
  init: 'main page',
  bye: 'you are all done page',
});

const WizardButton = ({ wizardModalState, onNext, ...extra }) => <Button {...extra} />;

const DepositRefundOnlineWizardPresenter = ({ onClose, value, amount, ...extra }) => {
  const [state, setState] = useStateWhileMounted(STATE.init);
  const { t } = useTranslation();

  const handleState = useCallback(({ value }) => void setState(value), [setState]);
  const transitionToInit = useCallback(() => void setState(STATE.init), [setState]);
  const [refundedAmount, setRefundedAmount] = useStateWhileMounted(0);

  const handleSuccess = useCallback((ev) => void setRefundedAmount(refundedAmount + ev?.amount), [refundedAmount, setRefundedAmount]);

  const isComplete = refundedAmount >= amount;
  const info = useMemo(() => ({ isComplete, amount, refundedAmount, close: true }), [amount, isComplete, refundedAmount]);

  const finish = useCallback(() => {
    call(`${CTAG}.onClose`, onClose, info);
    transitionToInit();
  }, [info, onClose, transitionToInit]);

  const handleClose = useCallback(() => {
    if (isComplete) {
      finish();
    } else {
      setState(STATE.bye);
    }
  }, [isComplete, finish, setState]);

  L.debug('DepositRefundOnlineWizardPresenter()', value, amount, extra);

  return (
    <div data-bem="DepositRefundOnlineWizard">
      <WizardModal tag="DepositRefundOnlineWizard" state={state} onChange={handleState} isOpen>
        <WizardModal.State value={STATE.init}>
          <WizardModal.Header>
            <h2 className={CN.title}>
              {t('balanceSheet.depositRefundWizard.titlePart1')} <CurrencySign value={amount} /> {t('balanceSheet.depositRefundWizard.titlePart2')}
            </h2>
            <div className={CN.subTitle}>
              {t('balanceSheet.depositRefundWizard.description1Part1')}
              <span className={CN.highlight}>{t('balanceSheet.depositRefundWizard.description1Part2')}</span>
              {t('balanceSheet.depositRefundWizard.description1Part3')}
            </div>
          </WizardModal.Header>
          <WizardModal.Body>
            <div className={CN.borderContainer}>
              {asArray(value).map((item, index) => (
                <DepositRefundItem key={createNodeKey({ index, unique: item?.intentId })} intentId={item?.intentId} amount={item?.amountToRefund} onRefund={handleSuccess} />
              ))}
            </div>
          </WizardModal.Body>
          <WizardModal.Footer>
            <div className={CN.footerText}>
              {t('balanceSheet.depositRefundWizard.totalRefunded')}
              <div className={CN.highlight}>
                <CurrencySign value={refundedAmount} />
              </div>
            </div>
            <WizardButton variant="primaryForce" onClick={handleClose}>
              {t('common.buttons.closeUpp')}
            </WizardButton>
          </WizardModal.Footer>
        </WizardModal.State>
        <WizardModal.State value={STATE.bye}>
          <WizardModal.Header>
            <h2 className={CN.title}>{t('balanceSheet.depositRefundWizard.title2')}</h2>
          </WizardModal.Header>
          <WizardModal.Body>
            <div className={CN.borderContainer}>
              <div className={CN.inner}>
                {t('balanceSheet.depositRefundWizard.description2Part1')}
                <div className={CN.highlight}>
                  <CurrencySign value={refundedAmount} />
                </div>
                {t('balanceSheet.depositRefundWizard.description2Part2')}
                <div className={CN.bold}>
                  <CurrencySign value={amount} />
                </div>
                {t('balanceSheet.depositRefundWizard.description2Part3')}
              </div>
            </div>
          </WizardModal.Body>
          <WizardModal.Footer>
            <WizardButton variant="iconOutline" onClick={transitionToInit}>
              <Icon variant="arrowBack" />
              {t('balanceSheet.depositRefundWizard.backButton')}
            </WizardButton>
            <WizardButton variant="primaryForce" onClick={finish}>
              {t('balanceSheet.depositRefundWizard.confirmButton')}
            </WizardButton>
          </WizardModal.Footer>
        </WizardModal.State>
      </WizardModal>
    </div>
  );
};

const DepositRefundOnlineWizardLoader = (props) => {
  const { source, amount, beDepositRefundInfo, clientId } = props ?? {};

  const { value, loading: isLoading } = useAsync(async () => {
    //
    if (!clientId || !source || !amount) {
      const info = { clientId, source, amount };
      return void L.warn('DepositRefundOnlineWizardLoader()', 'useAsync()', 'Missing argument(s):', info);
    }

    const result = await beDepositRefundInfo({ amount, source, clientId });
    const body = fromBeToBody(result);
    const value = fromDtoToDepositRefundInfo(body);

    const message = determineErrorMessage(result);
    if (message) L.warn('DepositRefundOnlineWizardLoader()', 'useAsync()', message, { value, body, result });

    return result.is.ok ? value : [];
  }, [amount, source, clientId, beDepositRefundInfo]);

  // NOTE: using memo here as to only print the message whenever the values are changed, not every component render
  useMemo(() => void L.debug('()', 'isLoading:', isLoading), [isLoading]);

  return isLoading ? <OverlaySpinner /> : <DepositRefundOnlineWizardPresenter {...props} value={value} />;
};

const mapDispatchToProps = { beDepositRefundInfo };

const DepositRefundOnlineWizardHoc = connect(null, mapDispatchToProps)(DepositRefundOnlineWizardLoader);

// noinspection JSUnusedGlobalSymbols
export default DepositRefundOnlineWizardHoc;
